/**
 * components/PWAPrompt.tsx
 * ------------------------------------------------------------------
 * PWA install prompt + offline indicator + update toast.
 *  - Android/Desktop: captures beforeinstallprompt, shows a card
 *  - iOS Safari: detects iOS + not-standalone, shows manual steps
 *  - Offline bar when navigator.onLine === false
 *  - Update toast when a new service worker is ready
 * Branded for Acepsle (orange).
 * ------------------------------------------------------------------ */
import { useCallback, useEffect, useState } from 'react';
import { PlusSquare, RefreshCw, Share, WifiOff, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'acepsle_pwa_dismiss';
const DISMISS_DAYS = 7;

function isIos(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

function isInStandaloneMode(): boolean {
  return (
    ('standalone' in window.navigator &&
      (window.navigator as unknown as { standalone: boolean }).standalone) ||
    window.matchMedia('(display-mode: standalone)').matches
  );
}

function wasDismissedRecently(): boolean {
  try {
    const dismissed = localStorage.getItem(DISMISS_KEY);
    return (
      !!dismissed && Date.now() - Number(dismissed) < DISMISS_DAYS * 86400000
    );
  } catch {
    return false;
  }
}

export default function PWAPrompt() {
  const [installEvent, setInstallEvent] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [showInstall, setShowInstall] = useState(false);
  const [showIosPrompt, setShowIosPrompt] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [updateReady, setUpdateReady] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      if (wasDismissedRecently()) return;
      setInstallEvent(e as BeforeInstallPromptEvent);
      setShowInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    if (isIos() && !isInStandaloneMode() && !wasDismissedRecently()) {
      const t = setTimeout(() => setShowIosPrompt(true), 2000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.addEventListener('updatefound', () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener('statechange', () => {
            if (nw.state === 'installed' && navigator.serviceWorker.controller) {
              setUpdateReady(true);
            }
          });
        });
      });
    }
  }, []);

  const handleInstall = useCallback(async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    if (outcome === 'accepted') setShowInstall(false);
    setInstallEvent(null);
  }, [installEvent]);

  const handleDismiss = useCallback(() => {
    setShowInstall(false);
    setShowIosPrompt(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <>
      {/* Android/Desktop install */}
      {showInstall && (
        <div className="fixed bottom-4 left-4 right-4 z-50 sm:left-auto sm:right-4 sm:w-96">
          <div className="rounded-card border border-black/5 bg-white p-4 shadow-pop dark:border-white/10 dark:bg-surfaceDark">
            <div className="flex items-start gap-3">
              <img
                src="/images/icon-192.png"
                alt="Acepsle"
                className="h-12 w-12 shrink-0 rounded-xl"
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  Install Acepsle Hub
                </p>
                <p className="mt-0.5 text-xs text-gray-500">
                  Add to your home screen for quick access and a native
                  app experience.
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <button
                    onClick={() => void handleInstall()}
                    className="rounded-none bg-brand px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:brightness-95"
                  >
                    Install
                  </button>
                  <button
                    onClick={handleDismiss}
                    className="px-3 py-2 text-xs text-gray-500 hover:text-gray-700"
                  >
                    Not now
                  </button>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="shrink-0 text-gray-300 hover:text-gray-500"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* iOS install instructions */}
      {showIosPrompt && (
        <div className="fixed bottom-4 left-4 right-4 z-50">
          <div className="rounded-card border border-black/5 bg-white p-4 shadow-pop dark:border-white/10 dark:bg-surfaceDark">
            <div className="flex items-start gap-3">
              <img
                src="/images/icon-192.png"
                alt="Acepsle"
                className="h-12 w-12 shrink-0 rounded-xl"
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  Install Acepsle Hub
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  To install this app on your iPhone or iPad:
                </p>
                <div className="mt-2 space-y-1.5">
                  <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <Share size={14} className="shrink-0 text-brand" />
                    <span>
                      Tap the <strong>Share</strong> button in Safari
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <PlusSquare size={14} className="shrink-0 text-brand" />
                    <span>
                      Tap <strong>"Add to Home Screen"</strong>
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleDismiss}
                  className="mt-3 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700"
                >
                  Got it
                </button>
              </div>
              <button
                onClick={handleDismiss}
                className="shrink-0 text-gray-300 hover:text-gray-500"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Offline indicator */}
      {!online && (
        <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-center gap-2 bg-amber-500 px-4 py-2 text-sm font-medium text-white">
          <WifiOff size={16} /> You're offline — some features may be
          unavailable
        </div>
      )}

      {/* Update toast */}
      {updateReady && (
        <div className="fixed right-4 top-4 z-50">
          <div className="flex items-center gap-3 rounded-card bg-primary px-4 py-3 text-white shadow-2xl">
            <RefreshCw size={16} className="shrink-0" />
            <span className="text-sm">A new version is available</span>
            <button
              onClick={() => window.location.reload()}
              className="rounded-none bg-white/20 px-3 py-1 text-xs font-semibold hover:bg-white/30"
            >
              Update
            </button>
          </div>
        </div>
      )}
    </>
  );
}
